import { HttpClient } from "../requests";
import ENDPOINTS from "../endpoints";
import useUserStore from "../../../state-management/useUserStore";
import {
	BankReportPaginator,
	CreatePayrollDto,
	CreatePayrollObject,
	DownloadReportPaginator,
	DownloadReportQueryDto,
	EmployeeAnnualTaxDeductionResponseDto,
	FileDownloadLink,
	MigrationDto,
	PayeSummaryPaginator,
	PayPeriod,
	PayrollRuns,
	PayrollRunsPaginator,
	PayrollRunStatusObject,
	PayrollSummaryApiResponse,
	PayslipPaginator,
	QueryCompanyPayslipsDto,
	QueryParam,
	QueryPayrollDto,
	QueryReportDto,
	SalaryAdvanceReportPaginator,
	SalaryReportPaginator,
	SavingSchemeReportResponsePaginator,
	SearchCompanyPayslipsDto,
	SearchReportDto,
	SSFResponsePaginator,
	TaxReportPaginator,
} from "../../../models/payroll.dto";
import { PayrollBatch } from "../../../models/payroll-batch.dto";

export class Payroll {
	getEmployeePayslips = (employeeId: number, query?: QueryParam) =>
		HttpClient.get<PayslipPaginator>(
			ENDPOINTS.PAYROLL.GET_EMPLOYEE_PAYSLIPS.replace(
				"{employeeId}",
				String(employeeId),
			),
			query,
		);
	getApprovedEmployeePayslips = (employeeId: number, query?: QueryParam) =>
		HttpClient.get<PayslipPaginator>(
			ENDPOINTS.PAYROLL.GET_APPROVED_EMPLOYEE_PAYSLIPS.replace(
				"{employeeId}",
				String(employeeId),
			),
			query,
		);
	getCompanyPayslips = (
		companyId: number,
		payPeriodId: number,
		query?: QueryCompanyPayslipsDto,
	) =>
		HttpClient.get<PayslipPaginator>(
			ENDPOINTS.PAYROLL.GET_COMPANY_PAYSLIPS.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	searchCompanyPayslips = (
		companyId: number,
		query?: SearchCompanyPayslipsDto,
	) =>
		HttpClient.get<PayslipPaginator>(
			ENDPOINTS.PAYROLL.SEARCH_COMPANY_PAYSLIPS.replace(
				"{companyId}",
				String(companyId),
			),
			query,
		);
	getPayrollRunParams = (payrollId: number, query: QueryParam) =>
		HttpClient.get<{ data: PayrollRuns }>(
			ENDPOINTS.PAYROLL.GET_PAYROLL_RUN_PARAMS.replace(
				"{payrollId}",
				String(payrollId),
			),
			query,
		);
	getCompanyPayrollRuns = (query?: QueryPayrollDto) =>
		HttpClient.get<PayrollRunsPaginator>(
			ENDPOINTS.PAYROLL.GET_COMPANY_PAYROLL_RUNS,
			query,
		);
	runPayrollParams = (payrollRunId: number, query: QueryParam) =>
		HttpClient.get<{ data: string }>(
			ENDPOINTS.PAYROLL.RUN_PAYROLL.replace(
				"{payrollRunId}",
				String(payrollRunId),
			),
			query,
		);
	createPayrollRun = (payload: CreatePayrollDto, query: QueryParam) =>
		HttpClient.post<{ data: CreatePayrollObject }>(
			ENDPOINTS.PAYROLL.CREATE_PAYROLL_RUN,
			payload,
			query,
		);

	getPayrollExcell = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<any>(
			ENDPOINTS.PAYROLL.GET_PAYROLL_EXCELL.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	getRecentPayrollRun = (companyId: number, query: QueryParam) =>
		HttpClient.get<{ data: PayPeriod }>(
			ENDPOINTS.PAYROLL.GET_RECENT_PAYROLL_RUN.replace(
				"{companyId}",
				String(companyId),
			),
			query,
		);
	getEmployeePayslip = (employeeId: number, payPeriodId: number) => {
		const currentUser = useUserStore.getState().currentUser;
		const isAdmin = currentUser?.user?.category?.toLowerCase() !== "employee";

		const urlTemplate = isAdmin
			? ENDPOINTS.PAYROLL.GET_EMPLOYEE_PAYSLIP_AS_PDF
			: ENDPOINTS.PAYROLL.GET_MY_PAYROLL_AS_PDF;

		const url = urlTemplate
			.replace("{employeeId}", String(employeeId))
			.replace("{payPeriodId}", String(payPeriodId));

		return HttpClient.get<{ data: string }>(url);
	};
	processPayrollPayment = (payrollRunId: number) =>
		HttpClient.post<PayrollBatch>(
			ENDPOINTS.PAYROLL.PROCESS_PAYROLL_PAYMENT.replace(
				"{payrollRunId}",
				String(payrollRunId),
			),
		);
	getPayrollBatchStatus = (batchId: number) =>
		HttpClient.get<PayrollBatch>(
			ENDPOINTS.PAYROLL.GET_PAYROLL_BATCH_STATUS.replace(
				"{batchId}",
				String(batchId),
			),
		);
	resumePayrollBatch = (batchId: number) =>
		HttpClient.post<{ message: string }>(
			ENDPOINTS.PAYROLL.RESUME_PAYROLL_BATCH.replace(
				"{batchId}",
				String(batchId),
			),
		);
	getFailedPayrollJobs = (batchId: number) =>
		HttpClient.get<any[]>(
			ENDPOINTS.PAYROLL.GET_FAILED_PAYROLL_JOBS.replace(
				"{batchId}",
				String(batchId),
			),
		);
	retrySkippedPayrollJobs = (batchId: number) =>
		HttpClient.post<{ message: string }>(
			ENDPOINTS.PAYROLL.RETRY_SKIPPED_PAYROLL_JOBS.replace(
				"{batchId}",
				String(batchId),
			),
		);
	retryFailedPayrollJobs = (batchId: number) =>
		HttpClient.post<{ message: string; retriedJobs: number }>(
			ENDPOINTS.PAYROLL.RETRY_FAILED_PAYROLL_JOBS.replace(
				"{batchId}",
				String(batchId),
			),
		);
	getPayrollRunStatus = (payrollRunId: number, query: QueryParam) =>
		HttpClient.get<{ data: PayrollRunStatusObject }>(
			ENDPOINTS.PAYROLL.GET_PAYROLL_RUN_STATUS.replace(
				"{payrollRunId}",
				String(payrollRunId),
			),
			query,
		);
	approvePayroll = (payPeriodId: number, query: QueryParam) =>
		HttpClient.get<{ data: any }>(
			ENDPOINTS.PAYROLL.GET_APPROVED_PAYROLL.replace(
				"{payPeriodId}",
				String(payPeriodId),
			),
			query,
		);
	runMigration = () =>
		HttpClient.get<MigrationDto>(ENDPOINTS.PAYROLL.RUN_MIGRATION);
	getSSFReport = (companyId: number, ssfName: string, query: QueryReportDto) =>
		HttpClient.get<SSFResponsePaginator>(
			ENDPOINTS.PAYROLL.GET_SSF_REPORT.replace(
				"{ssfName}",
				String(ssfName),
			).replace("{companyId}", String(companyId)),
			query,
		);
	getSalaryReport = (companyId: number, query: QueryReportDto) =>
		HttpClient.get<SalaryReportPaginator>(
			ENDPOINTS.PAYROLL.GET_SALARY_REPORT.replace(
				"{companyId}",
				String(companyId),
			),
			query,
		);
	getSalaryAdvanceReport = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<SalaryAdvanceReportPaginator>(
			ENDPOINTS.PAYROLL.GET_SALARY_ADVANCE_REPORT.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	getSalaryAdvanceReportExcel = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_SALARY_ADVANCE_REPORT_EXCEL.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	getSalaryAdvanceReportPdf = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_SALARY_ADVANCE_REPORT_PDF.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	getSavingSchemeReport = (companyId: number, query: QueryReportDto) =>
		HttpClient.get<SavingSchemeReportResponsePaginator>(
			ENDPOINTS.PAYROLL.GET_SAVING_SCHEME_REPORT.replace(
				"{companyId}",
				String(companyId),
			),
			query,
		);

	getBankReport = (
		companyId: number,
		payPeriodId: number,
		query: QueryReportDto,
	) =>
		HttpClient.get<BankReportPaginator>(
			ENDPOINTS.PAYROLL.GET_BANK_REPORT.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	downloadReport = (query: DownloadReportQueryDto) =>
		HttpClient.get<DownloadReportPaginator>(
			ENDPOINTS.PAYROLL.DOWNLOAD_REPORT,
			query,
		);
	getMyPayrollPdf = (payPeriodId: number, query: QueryParam) =>
		HttpClient.get<any>(
			ENDPOINTS.PAYROLL.GET_MY_PAYROLL_AS_PDF.replace(
				"{payPeriodId}",
				String(payPeriodId),
			),
			query,
		);
	getEmployeePayrollPdf = (
		employeeId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<any>(
			ENDPOINTS.PAYROLL.GET_EMPLOYEE_PAYSLIP_AS_PDF.replace(
				"{payPeriodId}",
				String(payPeriodId),
			).replace("{employeeId}", String(employeeId)),
			query,
		);
	getTaxReport = (companyId: number, payPeriodId: number, query: QueryParam) =>
		HttpClient.get<TaxReportPaginator>(
			ENDPOINTS.PAYROLL.GET_TAX_REPORT.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	getPayeSummary = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<PayeSummaryPaginator>(
			ENDPOINTS.PAYROLL.GET_PAYE_SUMMARY.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);

	getPayeSummaryPdf = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_PAYE_SUMMARY_PDF.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	// Returns List<String> of distinct years sorted ascending
	getPayPeriodYears = (companyId: number) =>
		HttpClient.get<string[]>(
			ENDPOINTS.PAYROLL.GET_PAY_PERIOD_YEARS.replace(
				"{companyId}",
				String(companyId),
			),
			{},
		);

	getEmployeeAnnualTaxDeduction = (
		companyId: number,
		employeeId: number,
		query: QueryParam & { year: string },
	) =>
		HttpClient.get<EmployeeAnnualTaxDeductionResponseDto>(
			ENDPOINTS.PAYROLL.GET_EMPLOYEE_ANNUAL_TAX_DEDUCTION.replace(
				"{companyId}",
				String(companyId),
			).replace("{employeeId}", String(employeeId)),
			query,
		);

	getEmployeeAnnualTaxDeductionPdf = (
		companyId: number,
		employeeId: number,
		query: QueryParam & { year: string },
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_EMPLOYEE_ANNUAL_TAX_DEDUCTION_PDF.replace(
				"{companyId}",
				String(companyId),
			).replace("{employeeId}", String(employeeId)),
			query,
		);
	getSalaryReportSearch = (companyId: number, query: SearchReportDto) =>
		HttpClient.get<SalaryReportPaginator>(
			ENDPOINTS.PAYROLL.GET_COMPANY_SALARY_SEARCH_REPORT.replace(
				"{companyId}",
				String(companyId),
			),
			query,
		);
	markPayrollRunDeleted = (
		companyId: number,
		payrollRunId: number,
		query: QueryParam,
	) =>
		HttpClient.delete<void>(
			ENDPOINTS.PAYROLL.MARK_PAYROLL_RUN_DELETED.replace(
				"{companyId}",
				String(companyId),
			).replace("{payrollRunId}", String(payrollRunId)),
			query,
		);
	deletePayrollRun = (
		companyId: number,
		payrollRunId: number,
		query: QueryParam,
	) =>
		HttpClient.delete<void>(
			ENDPOINTS.PAYROLL.DELETE_PAYROLL_RUN.replace(
				"{companyId}",
				String(companyId),
			).replace("{payrollRunId}", String(payrollRunId)),
			query,
		);
	getPayrollSummary = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<PayrollSummaryApiResponse>(
			ENDPOINTS.PAYROLL.GET_PAYROLL_SUMMARY.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	getPayrollSummaryExcel = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_PAYROLL_SUMMARY_EXCEL.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);

	getPayrollSummaryPdf = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_PAYROLL_SUMMARY_PDF.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	getEmployeeMasterListPdf = (companyId: number, sortBy?: string) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.EMPLOYEE_MASTER_LIST_PDF.replace(
				"{companyId}",
				String(companyId),
			),
			{
				sortBy,
			},
		);

	getNewHirePdf = (
		companyId: number,
		recordedAtGte?: string,
		recordedAtLte?: string,
	) => {
		const params: Record<string, string> = {};

		if (recordedAtGte) params["recordedAt.gte"] = recordedAtGte;
		if (recordedAtLte) params["recordedAt.lte"] = recordedAtLte;

		return HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.NEW_HIRE_PDF.replace("{companyId}", String(companyId)),
			params,
		);
	};

	getEmployeeExitPdf = (
		companyId: number,
		terminationDateGte?: string,
		terminationDateLte?: string,
	) => {
		const params: Record<string, string> = {};

		if (terminationDateGte) params["terminationDate.gte"] = terminationDateGte;
		if (terminationDateLte) params["terminationDate.lte"] = terminationDateLte;

		return HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.EMPLOYEE_EXIT_PDF.replace(
				"{companyId}",
				String(companyId),
			),
			params,
		);
	};

	getLeavesTakenPdf = (
		companyId: number,
		createdAtGte?: string,
		createdAtLte?: string,
		startDateGte?: string,
		startDateLte?: string,
		returnDateGte?: string,
		returnDateLte?: string,
	) => {
		const params: Record<string, string> = {};

		if (createdAtGte) params["createdAt.gte"] = createdAtGte;
		if (createdAtLte) params["createdAt.lte"] = createdAtLte;
		if (startDateGte) params["startDate.gte"] = startDateGte;
		if (startDateLte) params["startDate.lte"] = startDateLte;
		if (returnDateGte) params["returnDate.gte"] = returnDateGte;
		if (returnDateLte) params["returnDate.lte"] = returnDateLte;

		return HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.LEAVES_TAKEN_PDF.replace(
				"{companyId}",
				String(companyId),
			),
			params,
		);
	};
	getLeavesTakenEmployeePdf = (
		companyId: number,
		employeeId?: number,
		createdAtGte?: string,
		createdAtLte?: string,
		startDateGte?: string,
		startDateLte?: string,
		returnDateGte?: string,
		returnDateLte?: string,
	) => {
		const params: Record<string, string> = {};

		if (createdAtGte) params["createdAt.gte"] = createdAtGte;
		if (createdAtLte) params["createdAt.lte"] = createdAtLte;
		if (startDateGte) params["startDate.gte"] = startDateGte;
		if (startDateLte) params["startDate.lte"] = startDateLte;
		if (returnDateGte) params["returnDate.gte"] = returnDateGte;
		if (returnDateLte) params["returnDate.lte"] = returnDateLte;

		return HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.LEAVES_TAKEN_EMPLOYEE_PDF.replace(
				"{companyId}",
				String(companyId),
			).replace("{employeeId}", String(employeeId ?? "")),
			params,
		);
	};

	getLeavesBalancePdf = (
		companyId: number,
		startDate?: string,
		endDate?: string,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.LEAVES_BALANCE_PDF.replace(
				"{companyId}",
				String(companyId),
			),

			{ startDate, endDate },
		);

	getSSFReportExcel = (
		companyId: number,
		payPeriodId: number,
		ssfName: string,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_SSF_REPORT_EXCEL.replace(
				"{companyId}",
				String(companyId),
			)
				.replace("{payPeriodId}", String(payPeriodId))
				.replace("{ssfName}", encodeURIComponent(ssfName)),
			query,
		);

	getSSFReportPdf = (
		companyId: number,
		payPeriodId: number,
		ssfName: string,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_SSF_REPORT_PDF.replace(
				"{companyId}",
				String(companyId),
			)
				.replace("{payPeriodId}", String(payPeriodId))
				.replace("{ssfName}", encodeURIComponent(ssfName)),
			query,
		);

	getSavingSchemeReportExcel = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_SAVING_SCHEME_REPORT_EXCEL.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);

	getSavingSchemeReportPdf = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_SAVING_SCHEME_REPORT_PDF.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);

	getTaxReportExcel = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_TAX_REPORT_EXCEL.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);

	getTaxReportPdf = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_TAX_REPORT_PDF.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
	getSalaryReportExcel = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_SALARY_REPORT_EXCEL.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);

	getSalaryReportPdf = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_SALARY_REPORT_PDF.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);

	getBankReportExcel = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_BANK_REPORT_EXCEL.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);

	getBankReportPdf = (
		companyId: number,
		payPeriodId: number,
		query: QueryParam,
	) =>
		HttpClient.get<FileDownloadLink>(
			ENDPOINTS.PAYROLL.GET_BANK_REPORT_PDF.replace(
				"{companyId}",
				String(companyId),
			).replace("{payPeriodId}", String(payPeriodId)),
			query,
		);
}
